import React from 'react'
import { Link } from 'react-router-dom'
import Breadcrumb from '../../components/common/Breadcrumb'
import {
    IconCode,
    IconThumbUpFilled,
    IconStarFilled,
    IconFolderFilled,
    IconFileCode,
    IconBrandGit,
    IconLayout,
} from '@tabler/icons-react'
import { IconFolderOpen } from '@tabler/icons-react'
import { IconInfoCircle } from '@tabler/icons-react'
import { IconFileTypeJs } from '@tabler/icons-react'
import { IconDiamondFilled } from '@tabler/icons-react'
import { IconPalette } from '@tabler/icons-react'

export default function Documentation() {
    return (
        <>
            <Breadcrumb title="Documentation" path="Documentation" />
            <div className='flex flex-col gap-30'>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='flex gap-10 mb-30'>
                        <IconThumbUpFilled />
                        <p className='text-[20px]/[24px] font-medium'>
                            Getting Started
                        </p>
                    </div>
                    <div>
                        <p className='md:text-[20px]/[30px] mb-15'>
                            This guide will help you get started with <span className='text-danger'>LUCID!</span>
                        </p>
                        <p className='md:text-[20px]/[30px] mb-15'>
                            All the important stuff – compiling the source, file structure, build tools, file includes – is documented here.
                        </p>
                        <p className='md:text-[20px]/[30px] mb-15'>
                            If you really like our work, design, performance and support then <Link to="https://themeforest.net/" target='_blank' className='text-blue transition-all duration-300 hover:text-blue-100'> please don't forgot to rate us </Link> on Themeforest, it really motivate us to provide something better. &nbsp;
                            <span className='inline-flex gap-5 align-text-bottom'>
                                <IconStarFilled className='text-yellow md:w-[24px] md:h-[24px] w-[20px] h-[20px]' />
                                <IconStarFilled className='text-yellow md:w-[24px] md:h-[24px] w-[20px] h-[20px]' />
                                <IconStarFilled className='text-yellow md:w-[24px] md:h-[24px] w-[20px] h-[20px]' />
                                <IconStarFilled className='text-yellow md:w-[24px] md:h-[24px] w-[20px] h-[20px]' />
                                <IconStarFilled className='text-yellow md:w-[24px] md:h-[24px] w-[20px] h-[20px]' />
                            </span>
                        </p>
                    </div>
                </div>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='mb-30'>
                        <div className='flex gap-10 mb-5'>
                            <IconCode />
                            <p className='text-[20px]/[24px] font-medium'>
                                Development Setup
                            </p>
                        </div>
                        <p>
                            To get started, you need to do the following:
                        </p>
                    </div>
                    <div>
                        <p className='text-[20px]/[24px] mb-15'>
                            Project setup command
                        </p>
                        <ul className='pl-20 list-disc flex flex-col gap-10 text-[16px]/[20px]'>
                            <li>
                                Open the project folder in your terminal
                            </li>
                            <li>
                                Install node modules using npm <span className='text-danger'>npm i -f</span>
                            </li>
                            <li>
                                For run project in localhost enter command <span className='text-danger'>npm start</span>
                            </li>
                            <li>
                                Your project will open in your brwoser automatically ( if not opened enter URL in your browser <Link to="http://localhost:3000" target='_blank' className='text-blue transition-all duration-300 hover:text-blue-100'>http://localhost:3000</Link> )
                            </li>
                        </ul>
                    </div>
                </div>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='flex gap-10 mb-30'>
                        <IconFolderFilled />
                        <p className='text-[20px]/[24px] font-medium'>
                            Folder Structure
                        </p>
                    </div>
                    <div>
                        <ul>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconFolderFilled className='text-danger min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                <div>
                                    <span className='text-danger'>node_modules</span> ( NPM dependencies (by default the folder is not included) <span className='text-danger'>npm</span> installs dependencies. )
                                </div>
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconFolderFilled className='min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                public
                            </li>
                            <li className='mb-10 last:mb-0'>
                                <div className='flex gap-5 mb-10 last:mb-0'>
                                    - <IconFolderOpen className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                    src
                                </div>
                                <ul className='pl-20'>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFolderFilled className='min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        assets
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFolderFilled className='min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        components
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFolderFilled className='min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        pages
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        App.js
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        App.test.js
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        index.js
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        reportWebVitals.js
                                    </li>
                                    <li className='flex gap-5 mb-10 last:mb-0'>
                                        - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                        setupTests.js
                                    </li>
                                </ul>
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconBrandGit className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                .gitignore
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconFileCode className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                package-lock.json
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconFileCode className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                package.json
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconInfoCircle className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                README.md
                            </li>
                            <li className='flex gap-5 mb-10 last:mb-0'>
                                - <IconFileTypeJs className='stroke-[1.5] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px]' />
                                tailwind.config.js
                            </li>
                        </ul>
                    </div>
                </div>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='flex items-center gap-10 mb-30'>
                        <IconDiamondFilled />
                        <p className='text-[20px]/[24px] font-medium'>
                            References
                        </p>
                    </div>
                    <div className='overflow-auto'>
                        <table className='w-full min-w-[374px] table-striped text-[16px]/[24px]'>
                            <tbody>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Google font
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://fonts.google.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://fonts.google.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        ReactJS
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://reactjs.org/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://reactjs.org/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        jquery
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://jquery.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://jquery.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Tailwind CSS
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://tailwindcss.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://tailwindcss.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        NPM
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://www.npmjs.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://www.npmjs.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Apex Charts
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://apexcharts.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://apexcharts.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Tabler Icons
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://tabler.io/icons" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://tabler.io/icons
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Pexels
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://www.pexels.com/" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://www.pexels.com/
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        React Data Table
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://react-data-table-component.netlify.app/?path=/docs/getting-started-intro--docs" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://react-data-table-component.netlify.app/?path=/docs/getting-started-intro--docs
                                        </Link>
                                    </td>
                                </tr>
                                <tr>
                                    <td width={150} valign='top' className='p-3 border-t border-border'>
                                        Swiper Slider
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <Link to="https://swiperjs.com/react" target='_blank' className='text-blue transition-all duration-300 hover:text-primary'>
                                            https://swiperjs.com/react
                                        </Link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='flex items-center gap-10 mb-30'>
                        <IconLayout />
                        <p className='text-[20px]/[24px] font-medium'>
                            Layout Setting Class
                        </p>
                    </div>
                    <div className='overflow-auto'>
                        <table className='w-full min-w-[767px] table-hover text-[16px]/[24px]'>
                            <tbody>
                                <tr>
                                    <td className='p-3 border-t border-border'>
                                        Enable Light & Dark Mode!
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <div className='p-3 bg-black-50 text-white mb-10'>
                                            {'<html lang="en" data-theme="light">'}
                                        </div>
                                        <div>
                                            Change class in html tage <span className='text-danger'>.light, .dark</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr>
                                    <td className='p-3 border-t border-border'>
                                        Theme Color Setting
                                    </td>
                                    <td className='p-3 border-t border-border'>
                                        <div className='p-3 bg-black-50 text-white mb-10'>
                                            {'<body lucid-theme="default">'}
                                        </div>
                                        <div>
                                            Change class in body tage <span className='text-danger'>.default, .purple, .blue, .green, .orange, .blush</span>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className='bg-bg-dark shadow-light rounded-lg md:p-20 p-15'>
                    <div className='flex items-center gap-10 mb-30'>
                        <IconPalette />
                        <p className='text-[20px]/[24px] font-medium'>
                            Useful Color Class
                        </p>
                    </div>
                    <div className='mb-30'>
                        <p className='text-[16px]/[24px] mb-10'>
                            Background Colors
                        </p>
                        <div className='grid xxl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-20 mb-20'>
                            <div className='bg-primary text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .bg-primary
                            </div>
                            <div className='bg-dark text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .bg-dark
                            </div>
                        </div>
                        <div className='grid xxl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-20'>
                            <div className='bg-black text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .black
                            </div>
                            <div className='bg-black-50 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .black-50
                            </div>
                            <div className='bg-black-100 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .black-100
                            </div>
                            <div className='bg-white text-black text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .white
                            </div>
                        </div>
                    </div>
                    <div className='mb-30'>
                        <p className='text-[16px]/[24px] mb-10'>
                            Action Colors
                        </p>
                        <div className='grid xxl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-20'>
                            <div className='bg-success text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .success
                            </div>
                            <div className='bg-warning text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .warning
                            </div>
                            <div className='bg-danger text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .danger
                            </div>
                            <div className='bg-info text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .info
                            </div>
                            <div className='text-success text-center border border-dashed border-border text-[24px]/[24px] rounded-lg md:p-20 p-15'>
                                .text-success
                            </div>
                            <div className='text-warning text-center border border-dashed border-border text-[24px]/[24px] rounded-lg md:p-20 p-15'>
                                .text-warning
                            </div>
                            <div className='text-danger text-center border border-dashed border-border text-[24px]/[24px] rounded-lg md:p-20 p-15'>
                                .text-danger
                            </div>
                            <div className='text-info text-center border border-dashed border-border text-[24px]/[24px] rounded-lg md:p-20 p-15'>
                                .text-info
                            </div>
                        </div>
                    </div>
                    <div className=''>
                        <p className='text-[16px]/[24px] mb-10'>
                            Chart Colors
                        </p>
                        <div className='grid xxl:grid-cols-5 sm:grid-cols-2 grid-cols-1 gap-20 mb-10'>
                            <div className='bg-chart-color1 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .chart-color1
                            </div>
                            <div className='bg-chart-color2 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .chart-color2
                            </div>
                            <div className='bg-chart-color3 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .chart-color3
                            </div>
                            <div className='bg-chart-color4 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .chart-color4
                            </div>
                            <div className='bg-chart-color5 text-white text-center border border-dashed border-border text-[16px]/[24px] rounded-lg md:p-20 p-15'>
                                .chart-color5
                            </div>
                        </div>
                        <p className=''>
                            Note : Chart color change as per theme
                        </p>
                    </div>
                </div>
            </div>
        </>
    )
}
